/**
 * Die Sätze für den Anfang — und was der Beispielreise-Knopf sagt.
 *
 * ## Warum das eine eigene Datei ist
 *
 * Weil es genau die Sorte Text ist, die sonst an fünf Stellen im Markup
 * steht, an vier davon denselben Ton hat und an der fünften einen anderen.
 * Hier stehen sie beieinander, und man liest sie hintereinander wie eine
 * kleine Anleitung: Kette von Stationen → erste Station setzen → Route
 * rechnen → Tage lesen. Das ist der Weg, den ein Neuer nimmt, und diese Datei
 * ist der einzige Ort, an dem er als Ganzes sichtbar ist.
 *
 * Dazu die Prüfbarkeit (`tests/run.mjs` läuft ohne Browser): Was in einer
 * Vorlage steht, ist unerreichbar; eine Konstante hier ist eine gewöhnliche
 * Zeichenkette.
 *
 * ## Nur in der einfachen Ansicht
 *
 * Geschaltet wird das für die **zusätzlichen** Zeilen zentral über
 * `[data-dichte]` (§7 des Designsystems): Sie stehen im Markup in einem
 * Element mit der Klasse `nur-einfach`, und `token/anwendung.css` blendet sie
 * in der Expertenfassung aus. Kein Prop, kein Zustand, keine Verzweigung in
 * den Bauteilen.
 *
 * Die **eine** Ausnahme ist der Satz, der selbst ein Leerzustand ist (die
 * leere Stationsliste): Er kann nicht verschwinden, denn eine leere Fläche
 * ohne Text sieht aus wie ein Fehler. Dort gibt es denselben Platz in zwei
 * Längen — `leerzustandstext()` wählt sie, und deshalb nimmt genau diese
 * Funktion die Dichte als Argument.
 *
 * Es ist Anzeigedichte und kein Schutz: Die Sätze stehen im ausgelieferten
 * HTML. Das ist in Ordnung, denn sie enthalten nichts als Anleitung.
 *
 * ## Was hier ausdrücklich nicht steht
 *
 * Die zwei Stellen, die den Satz schon **hatten**, als dieser Vertrag
 * geschrieben wurde: die Vorbereitung ohne Stationen (`OHNE_STATIONEN` in
 * `VorbereitungPanel.svelte`) und der Einstieg in die Grundlagen (der `claim`
 * in `Grundlagen.svelte`). Beide sagen dasselbe wie die Sätze hier, beide
 * stehen an ihrer Fläche — und beide gelten in **jeder** Dichte, weil sie die
 * Fläche erklären und nicht den Anfang. Sie hierher zu ziehen hieße, zwei
 * funktionierende Texte umzubauen, damit eine Liste vollständig aussieht.
 */

import { type Dichte } from './dichte';

/** Wo ein Erklärsatz steht. */
export type Erklaerstelle =
  | 'reiseliste'
  | 'stationen-leer'
  | 'plan-ohne-route';

/**
 * Die Sätze selbst — je Stelle einer.
 *
 * Einer und nicht zwei: An einem Leerzustand steht schon ein Titel und ein
 * Satz, der sagt, was zu tun ist. Was hier dazukommt, ist die **Einordnung**
 * („was ist das überhaupt"), und die verträgt keinen zweiten Absatz.
 */
export const ERKLAERSATZ: Record<Erklaerstelle, string> = {
  // Der erste Satz, den ein neues Konto überhaupt liest. Er sagt, was eine
  // Reise in diesem Werkzeug ist — nicht ein Termin, nicht ein Ziel, sondern
  // eine Kette. Daran hängt der Rest der Oberfläche: Stationen, Etappen, Tage.
  reiseliste:
    'Eine Reise ist eine Kette von Stationen: Du setzt die Orte, die Anwendung rechnet Strecke, Fahrzeit und Tage dazwischen.',
  // Die Stelle, an der Neue am längsten stehen bleiben: Die Karte ist da, die
  // Liste ist leer, und der Knopf, der hilft, ist ein Suchfeld am oberen Rand.
  'stationen-leer':
    'Erste Station: Ort oben rechts suchen oder auf die Karte klicken. Die Reihenfolge änderst du später durch Ziehen.',
  // Der Plan-Reiter ist der einzige, der ohne eigenen Knopf leer bleibt — die
  // Route wird eine Fläche weiter links gerechnet. Ohne diesen Satz sieht er
  // aus wie eine Funktion, die nicht geht.
  'plan-ohne-route':
    'Route rechnen, dann entstehen Tage: Aus Stationen und Startdatum wird der Ablauf — wann du wo bist und wo der Tag endet.'
};

/**
 * Die knappe Fassung — für die Expertenansicht.
 *
 * Nur dort, wo der Satz **ein Leerzustand** ist und nicht eine Zeile daneben:
 * Ein Leerzustand muss in jeder Dichte etwas sagen (eine leere Fläche ohne
 * Text sieht aus wie ein Fehler), und zwei fast gleiche Sätze übereinander
 * wären schlimmer als einer. Also derselbe Platz, zwei Längen — genau die
 * Regel von §7: einfach erklärt, Experte knapp.
 *
 * Wo hier nichts steht, gilt der ausführliche Satz — und er steht dann in
 * einem Element mit der Klasse `nur-einfach`, das in der Expertenfassung
 * ganz verschwindet.
 */
export const ERKLAERSATZ_KNAPP: Partial<Record<Erklaerstelle, string>> = {
  'stationen-leer':
    'Klick auf die Karte oder such oben nach einem Ort. Die Reihenfolge änderst du später durch Ziehen.'
};

/**
 * Der Text für einen Leerzustand, passend zur Dichte.
 *
 * Die Entscheidung steht hier und nicht als Bedingung im Markup: Sie ist eine
 * Regel über Text, und im Markup wäre sie ohne Browser nicht prüfbar.
 */
export function leerzustandstext(stelle: Erklaerstelle, dichte: Dichte): string {
  if (dichte === 'experte') return ERKLAERSATZ_KNAPP[stelle] ?? ERKLAERSATZ[stelle];
  return ERKLAERSATZ[stelle];
}

/* ══ Der Knopf „Beispielreise anlegen" ═══════════════════════════════════
 *
 * `POST /trips/beispiel` kopiert die Beispielreise des Betreibers ins eigene
 * Konto — dieselbe Kopie, die eine Einladung mitbringt. Höchstens eine je
 * Konto; der zweite Versuch ist ein 409 und **kein Fehler**, sondern eine
 * Auskunft.
 */

/** Der Text am Knopf — an einer Stelle, weil er an zwei Flächen steht. */
export const BEISPIEL_KNOPF = 'Beispielreise anlegen';

/**
 * Was der Knopf verspricht.
 *
 * „ansehen und verändern" ist der Punkt: Es ist eine **Kopie** im eigenen
 * Konto und kein Schaufenster. Wer das nicht weiß, traut sich nicht, darin
 * herumzuräumen — und genau das ist der Zweck.
 */
export const BEISPIEL_WIRKUNG =
  'Eine fertige Reise als Kopie in deinem Konto — zum Ansehen, Verändern und Wegwerfen. Sie kommt einmal je Konto.';

/**
 * Die Antwort des Servers in einen Satz.
 *
 * Drei Fälle, und nur einer ist eine Störung:
 *
 * - **409** — die Kopie liegt schon. Ruhig, keine rote Zeile: Es ist genau
 *   das, was der Endpunkt verspricht, und die Reise steht in der Liste
 *   darunter.
 * - **404** — auf diesem Server ist keine Beispielreise hinterlegt. Auch das
 *   ist keine Störung, sondern eine Einrichtungsfrage des Betreibers; ein
 *   „noch einmal versuchen" wäre ein Knopf, der zuverlässig nichts ändert.
 * - **alles andere** — eine Störung. Dann gilt der Satz des Servers, wenn er
 *   einen mitschickt, und sonst der allgemeine.
 */
export function beispielmeldung(status: number | null | undefined, satz?: string | null): string {
  if (status === 409) {
    return 'Die Beispielreise liegt schon in deinem Konto — sie steht in der Liste. Es gibt sie einmal je Konto.';
  }
  if (status === 404) {
    return 'Auf diesem Server ist keine Beispielreise hinterlegt. Das entscheidet der Betreiber; an deinen Reisen ändert es nichts.';
  }
  return satz?.trim() || 'Die Beispielreise ließ sich nicht anlegen.';
}

/**
 * Ist die Antwort eine Auskunft statt einer Störung?
 *
 * Entscheidet die Tonlage: `merk` (ruhig, gelb) gegen `ernst` (rot mit
 * Ausweg). Ein 409 rot zu zeigen wäre die Behauptung, etwas sei kaputt — und
 * kaputt ist nichts, die Reise ist bloß schon da.
 */
export function istRuhig(status: number | null | undefined): boolean {
  return status === 409 || status === 404;
}
